// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { Users, Phone, Mail, Building2, User as UserIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { MobileSearchBar } from '../ui/MobileSearchBar';
import { MobileListItem } from '../ui/MobileListItem';
import { MobileEmptyState } from '../ui/MobileEmptyState';
import { MobilePageContainer } from '../ui/MobilePageContainer';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { MobileFab } from '../ui/MobileFab';
import MobileClientQuickForm from './MobileClientQuickForm';

type Client = {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  mobile?: string;
  cpf_cnpj?: string;
  client_type?: 'person' | 'company';
  client_priority?: string;
  address?: string;
  city?: string;
  state?: string;
};

const PRIORITY_TONES: Record<string, string> = {
  vip: 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-300',
  high: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  medium: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  low: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const MobileClients = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Client | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('clients')
        .select(
          'id, name, email, phone, mobile, cpf_cnpj, client_type, client_priority, address, city, state',
        )
        .eq('user_id', user.id)
        .order('name', { ascending: true });
      if (error) throw error;
      setClients(data || []);
    } catch (err: any) {
      toast({
        title: 'Erro ao carregar clientes',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.name, c.email, c.phone, c.mobile, c.cpf_cnpj]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [clients, search]);

  return (
    <MobilePageContainer>
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur px-3 pt-3 pb-2 border-b border-border">
        <MobileSearchBar
          value={search}
          onChange={setSearch}
          placeholder="Buscar cliente, email, CPF..."
        />
        <div className="mt-2 px-1 text-[11px] text-muted-foreground">
          {loading ? 'Carregando...' : `${filtered.length} cliente${filtered.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      {!loading && filtered.length === 0 ? (
        <MobileEmptyState
          className="mt-8"
          icon={<Users className="h-6 w-6" />}
          title="Nenhum cliente encontrado"
          description={
            search
              ? 'Tente outra busca.'
              : 'Cadastre seu primeiro cliente no botão +.'
          }
          action={
            !search && (
              <Button onClick={() => setCreateOpen(true)}>Novo cliente</Button>
            )
          }
        />
      ) : (
        <div className="divide-y divide-border bg-card">
          {filtered.map((c) => {
            const isCompany = c.client_type === 'company';
            const prTone = c.client_priority
              ? PRIORITY_TONES[c.client_priority]
              : null;
            const phone = c.mobile || c.phone;
            return (
              <MobileListItem
                key={c.id}
                icon={
                  <span className="text-xs font-bold">
                    {getInitials(c.name || '?')}
                  </span>
                }
                iconBg={
                  isCompany
                    ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
                    : 'bg-primary/15 text-primary'
                }
                title={c.name}
                subtitle={
                  <>
                    {phone && <span>{phone}</span>}
                    {phone && c.email && <span> · </span>}
                    {c.email && <span className="truncate">{c.email}</span>}
                  </>
                }
                badges={
                  <>
                    <Badge
                      className={`${
                        isCompany
                          ? 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
                          : 'bg-primary/15 text-primary'
                      } border-0 text-[10px]`}
                    >
                      {isCompany ? (
                        <>
                          <Building2 className="h-3 w-3 mr-1" /> PJ
                        </>
                      ) : (
                        <>
                          <UserIcon className="h-3 w-3 mr-1" /> PF
                        </>
                      )}
                    </Badge>
                    {prTone && (
                      <Badge className={`${prTone} border-0 text-[10px] uppercase`}>
                        {c.client_priority}
                      </Badge>
                    )}
                  </>
                }
                onClick={() => setSelected(c)}
              />
            );
          })}
        </div>
      )}

      {/* Detalhes / ações rápidas */}
      <Sheet open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>{selected?.name}</SheetTitle>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-3">
              {selected.cpf_cnpj && (
                <div className="flex items-center justify-between text-sm border-b border-border pb-2">
                  <span className="text-muted-foreground">
                    {selected.client_type === 'company' ? 'CNPJ' : 'CPF'}
                  </span>
                  <span className="font-mono">{selected.cpf_cnpj}</span>
                </div>
              )}
              {(selected.city || selected.state) && (
                <div className="flex items-center justify-between text-sm border-b border-border pb-2">
                  <span className="text-muted-foreground">Cidade</span>
                  <span>
                    {[selected.city, selected.state].filter(Boolean).join(' / ')}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-2 gap-2 pt-2">
                {(selected.mobile || selected.phone) && (
                  <Button
                    asChild
                    variant="outline"
                    className="h-12 justify-start gap-2"
                  >
                    <a href={`tel:${selected.mobile || selected.phone}`}>
                      <Phone className="h-4 w-4 text-emerald-500" />
                      <span className="truncate">Ligar</span>
                    </a>
                  </Button>
                )}
                {(selected.mobile || selected.phone) && (
                  <Button
                    asChild
                    variant="outline"
                    className="h-12 justify-start gap-2"
                  >
                    <a
                      href={`https://wa.me/55${(selected.mobile || selected.phone)?.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Phone className="h-4 w-4 text-emerald-600" />
                      <span className="truncate">WhatsApp</span>
                    </a>
                  </Button>
                )}
                {selected.email && (
                  <Button
                    asChild
                    variant="outline"
                    className="h-12 justify-start gap-2 col-span-2"
                  >
                    <a href={`mailto:${selected.email}`}>
                      <Mail className="h-4 w-4 text-sky-500" />
                      <span className="truncate">{selected.email}</span>
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <MobileFab onClick={() => setCreateOpen(true)} label="Novo" />

      <MobileClientQuickForm
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => load()}
      />
    </MobilePageContainer>
  );
};

export default MobileClients;
